import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { FINANCIAL_ROUNDING_CONFIG } from 'partner360/configs/financial/financialFormatting'
import { getAutoRounding } from 'partner360/helpers/getAutoRounding'
import { formatSobValue } from 'partner360/lib/metricFormatting'

import { COCKPIT_FIN_FRACTION_DIGITS } from '../constants'

/** Placeholder for a metric with no data behind it, per the cockpit design. */
export const EMPTY_COCKPIT_VALUE = '--'

/** Compact money for cockpit tiles: one decimal place for thousands and millions. */
export const formatCockpitMoney = (value: number | null, currency: string) => {
  if (value === null || !Number.isFinite(value)) {
    return EMPTY_COCKPIT_VALUE
  }

  const { divisor, suffix } = FINANCIAL_ROUNDING_CONFIG[getAutoRounding(value)]
  const fractionDigits = divisor === 1 ? 0 : COCKPIT_FIN_FRACTION_DIGITS

  const formatted = getFormattedFinance(value / divisor, {
    withCurrencySign: currency === 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

  return `${formatted}${suffix}`
}

/** The signed money behind a growth rate, for the figure shown in brackets beside it — e.g. the
 *  `+18.9K` of `+3,5% (+18.9K)`. `formatCockpitMoney`'s compact rounding without the currency sign:
 *  the cell it appears in is already a rate, and the Baseline/Plan columns beside it carry the unit.
 *  Takes `formatCockpitPercentChange`'s sign rule so both halves of that cell agree on `+0`. */
export const formatCockpitMoneyDelta = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) {
    return EMPTY_COCKPIT_VALUE
  }

  const { divisor, suffix } = FINANCIAL_ROUNDING_CONFIG[getAutoRounding(value)]
  const fractionDigits = divisor === 1 ? 0 : COCKPIT_FIN_FRACTION_DIGITS

  const formatted = getFormattedFinance(value / divisor, {
    withCurrencySign: false,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

  // `getFormattedFinance` already carries the minus sign, so only gains (and no change) need one.
  return value < 0 ? `${formatted}${suffix}` : `+${formatted}${suffix}`
}

/** A score badge always reads a number: a score with nothing behind it is a 0, never the `--` that
 *  value cells use. Band color and text both come off this, so an absent score wears the same pill
 *  a real 0 does. */
export const toBadgeScore = (value: number | null) =>
  value === null || !Number.isFinite(value) ? 0 : value

/** Whole-number score used in compact quality badges. */
export const formatCockpitScore = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) {
    return EMPTY_COCKPIT_VALUE
  }

  return String(Math.round(value))
}

export const formatCockpitPercent = (value: number | null) => {
  const formatted = formatSobValue(value)

  return formatted === null ? EMPTY_COCKPIT_VALUE : `${formatted}%`
}

export const formatCockpitPercentChange = (value: number | null, isSob: boolean = false) => {
  const formatted = formatSobValue(value)
  const sign = isSob ? 'pp' : '%'

  if (formatted === null || value === null) {
    return EMPTY_COCKPIT_VALUE
  }

  // `formatSobValue` already carries the minus sign, so only gains (and no change) need a prefix.
  return value < 0 ? `${formatted}${sign}` : `+${formatted}${sign}`
}
